<script setup lang="ts">
  import { useGetAccount } from "@/api/account/account.ts"
  import {
    getGetCustomerQueryKey,
    useUpdateCustomer,
  } from "@/api/customer/customer.ts"
  import { type Address } from "@/api/model"
  import { aggregateQueries } from "@/lib/aggregateQueries.ts"
  import { useGetAccountId } from "@/lib/useGetAccountId.ts"
  import { formatCurrency } from "@/lib/utils.ts"
  import { useQueryClient } from "@tanstack/vue-query"
  import { useSessionStorage } from "@vueuse/core"
  import { computed, ref, watch } from "vue"
  import ErrorMessage from "@/components/ErrorMessage.vue"
  import { Alert, AlertDescription } from "@/components/ui/alert"
  import { Badge } from "@/components/ui/badge"
  import { Button } from "@/components/ui/button"
  import { DialogFooter } from "@/components/ui/dialog"
  import { RadioCardGroup, RadioCardItem } from "@/components/ui/radio-card"
  import { Skeleton } from "@/components/ui/skeleton"

  interface AccountOption {
    id: string
    accountNumber: string
    supplyAddress: Address | undefined
    balance: string
    isClosed: boolean | undefined
  }

  defineProps<{
    open: boolean
  }>()

  const emit = defineEmits<{
    "update:open": [value: boolean]
  }>()

  const accountIdQuery = useGetAccountId()
  const accountIds = computed(() => accountIdQuery.data.value?.accountIDs || [])

  const accountQueries = accountIds.value.map((id: number) =>
    useGetAccount(computed(() => id)),
  )

  const { isError, isLoading } = aggregateQueries([
    accountIdQuery,
    ...accountQueries,
  ])

  const accounts = computed((): AccountOption[] => {
    return accountQueries.map((query) => {
      const account = query.data.value

      return {
        id: account?.accountID?.toString() ?? "",
        accountNumber: account?.accountNumber ?? "",
        supplyAddress: account?.supplyAddress,
        balance:
          typeof account?.balance === "number"
            ? // Reverse sign in front of balance value as BE returns negative for credit and positive for debit
              formatCurrency(account.balance, { negate: true })
            : "",
        isClosed: account?.isClosed,
      }
    })
  })

  const selectedAccountId = ref(
    accountIdQuery.data.value?.accountIDLastVisited?.toString() ?? "",
  )

  watch(
    () => accountIdQuery.data.value?.accountIDLastVisited,
    (newValue) => {
      if (newValue?.toString() !== selectedAccountId.value) {
        selectedAccountId.value = newValue?.toString() ?? ""
      }
    },
    { immediate: true },
  )

  const formatAddress = (address: Address | undefined) => {
    if (!address) return ""

    return [
      address.addressLine1,
      address.addressLine2,
      address.addressLine3,
      address.postcode,
    ]
      .filter(Boolean)
      .join(", ")
  }

  const {
    mutate,
    isPending,
    isError: isUpdateError,
    error,
  } = useUpdateCustomer()

  const queryClient = useQueryClient()

  const handleSubmit = () => {
    const customerId = accountIdQuery.data.value?.customerID
    const currentAccountId =
      accountIdQuery.data.value?.accountIDLastVisited?.toString()

    if (!customerId || !selectedAccountId.value) return
    if (selectedAccountId.value === currentAccountId) {
      emit("update:open", false)
      return
    }

    mutate(
      {
        customerId,
        data: {
          accountIDLastVisited: Number(selectedAccountId.value),
        },
      },
      {
        onSuccess: () => {
          queryClient.resetQueries({
            queryKey: getGetCustomerQueryKey(),
          })
          useSessionStorage("app_storage_cache", "").value = undefined
          emit("update:open", false)
        },
      },
    )
  }
</script>

<template>
  <div class="overflow-y-auto p-[1px]">
    <ErrorMessage v-if="isError" />
    <RadioCardGroup
      v-else
      v-model="selectedAccountId"
      ariaLabelledby="account-selection-heading"
      @keydown.enter.prevent="handleSubmit"
    >
      <RadioCardItem
        v-for="account in accounts"
        :key="account.id"
        :value="account.id"
        :disabled="isPending"
        name="account-selection"
      >
        <template #header>
          Account:
          <Skeleton v-if="isLoading" class="ml-1 inline-block h-3 w-24" />
          <template v-else>
            {{ account.accountNumber }}
          </template>
          <Badge
            v-if="account.isClosed"
            variant="destructive"
            class="ml-2 hidden sm:inline-flex"
          >
            Closed
          </Badge>
        </template>
        <template #subheader>
          <Skeleton v-if="isLoading" class="h-4 w-72" />
          <template v-else>
            {{
              account.supplyAddress ? formatAddress(account.supplyAddress) : ""
            }}
          </template>
        </template>
        <div class="flex">
          <p>
            Balance:
            <Skeleton v-if="isLoading" class="ml-1 inline-block h-3 w-14" />
            <template v-else>{{ account.balance }}</template>
          </p>
          <Badge
            v-if="account.isClosed"
            variant="destructive"
            class="ml-auto sm:hidden"
          >
            Closed
          </Badge>
        </div>
      </RadioCardItem>
    </RadioCardGroup>
  </div>

  <DialogFooter class="mt-0 flex-col sm:flex-col">
    <Alert v-if="isUpdateError" variant="error">
      <AlertDescription>
        <template v-if="error?.message === 'Network Error'">
          Cannot reach the So Energy server
        </template>
        <template v-else>Unknown error!</template>
      </AlertDescription>
    </Alert>
    <Button
      variant="primary"
      :disabled="isLoading || isError"
      :loading="isPending"
      size="xl"
      class="w-full"
      @click="handleSubmit"
    >
      Next
    </Button>
  </DialogFooter>
</template>
